// Import 3rd-party dependencies
import { Component, OnInit } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition
} from '@angular/material/snack-bar';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

// Import project-specific dependencies
import { FavoriteService, IFavoriteResourceLink } from '@app/services/favorites/favorites.service';
import { AppToolsService } from '@app/services/app-tools/app-tools.service';
import {
  FavoriteSnackbarComponent,
  FavoriteSnackBarData,
  EFavoriteSnackBarAction
} from '@app/shared/components/snackbar/favorite-snackbar.component';
import { TOOLS_RESOURCES_COMPONENT_CONSTS } from '@app/config/constants';

@Component({
  selector: 'app-tools-and-resources',
  templateUrl: './tools-and-resources.component.html',
  styleUrls: ['./tools-and-resources.component.css']
})
export class ToolsAndResourcesComponent implements OnInit {
  private searchAction$ = new BehaviorSubject<string>('');

  // Class attributes
  snackBarAction: number;
  searchValue: string;

  consts = TOOLS_RESOURCES_COMPONENT_CONSTS;

  resourceList$ = combineLatest([
    this.searchAction$,
    this.appToolsService.allResourceLinks$,
    this.favoriteService.favorites$
  ]).pipe(
    map(([search, links, fav]) => {
      const sortedAlpha = links
        .map((l) => {
          const idx = fav.findIndex((f) => f.name === l.name && f.customLink === false);
          const tool = {
            name: l.name,
            link: l.link,
            favorite: idx > -1 ? true : false,
            keywords: l.keywords
          };
          return tool;
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      search = search.trim();
      if (search.length === 0) {
        return sortedAlpha;
      } else {
        return sortedAlpha.filter((a) =>
          !a.keywords
            ? a.name.toLocaleLowerCase().includes(search)
            : a.name.toLocaleLowerCase().includes(search) || a.keywords.toLocaleLowerCase().includes(search)
        );
      }
    })
  );

  // Class Methods
  constructor(
    private appToolsService: AppToolsService,
    private _snackBar: MatSnackBar,
    private favoriteService: FavoriteService,
    private _copySnackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.favoriteService.getFavoriteList();
  }

  searchLinks(): void {
    this.searchAction$.next(this.searchValue.toLowerCase());
  }

  openLink(link: string): void {
    window.open(link, '_blank');
  }

  favoriteLink(link: IFavoriteResourceLink): void {
    link.customLink = false;
    link.dateTimeFavorited = new Date();
    this.favoriteService.addFavorite([link]);
    this.openSnackBar(link, EFavoriteSnackBarAction.Favorite);
  }

  unfavoriteLink(link: IFavoriteResourceLink): void {
    this.favoriteService.removeFavorite(link);
    this.openSnackBar(link, EFavoriteSnackBarAction.Unfavorite);
  }

  openSnackBar(linkInfo: IFavoriteResourceLink, action: number, message?: string): void {
    const snackBarData: FavoriteSnackBarData = {
      linkInfo,
      action,
      message
    };
    const config: MatSnackBarConfig = {
      horizontalPosition: this.consts.SNACKBAR_HORIZONTAL_POS as MatSnackBarHorizontalPosition,
      verticalPosition: this.consts.SNACKBAR_VERTICAL_POS as MatSnackBarVerticalPosition,
      panelClass: 'favorite-snackbar',
      duration: this.consts.SNACKBAR_DURATION,
      data: snackBarData
    };
    this._snackBar.openFromComponent(FavoriteSnackbarComponent, config);
  }

  resetInputField(term: string): void {
    this.searchAction$.next(term || '');
  }

  openCopySnackBar(): void {
    this._copySnackBar.open(this.consts.MESSAGE_COPY_LINK, undefined, {
      duration: this.consts.COPY_SNACKBAR_DURATION
    });
  }

}
